<template>
  <Popover
    v-slot="{ open }"
    class="inline-flex relative"
  >
    <slot
      v-if="slots.default"
      :open="open"
    />
    <template v-else>
      <div
        v-tooltip="'Map layers'"
        class="lg:inline-flex xl:hidden"
      >
        <PopoverButton
          :class="[
            'inline-flex justify-center items-center p-sm text-sm rounded-md font-medium border',
            'focus-visible:ring-offset-2 focus-visible:ring-2 focus-visible:outline-none',
            (activeMapLayer !== 'default' || open)
              ? 'border-primary-600 bg-primary-600 text-white hover:bg-primary-700 active:bg-primary-800 focus-visible:ring focus-visible:ring-white focus-visible:outline-primary-800'
              : 'border-default-300 bg-white text-default-700 active:bg-default-300 hover:bg-default-50 focus-visible:ring-offset-2 focus-visible:ring-offset-white focus-visible:ring-2 focus-visible:ring-primary-500',
          ]"
        >
          <FontAwesomeIcon
            :icon="faLayerGroup"
            aria-hidden="true"
            class="w-4 h-4"
          />
          <span class="sr-only">
            Map layers
          </span>
        </PopoverButton>
      </div>
      <div class="lg:hidden xl:inline-flex">
        <PopoverButton
          :as="SpectreButton"
          :color="(activeMapLayer !== 'default' || open) ? 'primary' : 'secondary'"
          :leading-icon="faLayerGroup"
        >
          Map layers
        </PopoverButton>
      </div>
    </template>
    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <PopoverPanel
        :class="[
          'absolute  top-full z-10 w-screen max-w-xs transform',
          direction === 'bottom' && 'left-full -translate-x-full mt-large',
          direction === 'right' && 'right-full mr-large -translate-y-1/2',
        ]"
      >
        <section class="divide-y bg-white rounded-lg shadow-lg ring-1 ring-black/5 h-96 overflow-y-auto px-base py-large space-y-large">
          <article>
            <RadioGroup
              v-model="selectedMapStyle"
              by="id"
              class="space-y-large"
              @update:model-value="handleStyleChange"
            >
              <RadioGroupLabel class="font-sans font-medium text-sm text-default-900">
                Map type
              </RadioGroupLabel>
              <div class="grid grid-cols-3 gap-base">
                <div
                  v-for="style in MapboxStyleSources"
                  :key="style.id"
                  class="col-span-1 text-center"
                >
                  <RadioGroupOption
                    v-slot="{ checked }"
                    as="template"
                    :value="style"
                  >
                    <button
                      :class="[
                        'w-full cursor-pointer rounded-md text-center focus:outline-none ring-2 ring-offset-2 focus-visible:ring-primary-700',
                        checked ? 'ring-primary-500' : 'ring-transparent',
                      ]"
                    >
                      <img
                        :src="style.image"
                        class="w-full rounded-md"
                        :alt="style.label"
                      >
                    </button>
                  </RadioGroupOption>
                  <span class="pt-2 block w-full text-xs text-default-700">
                    {{ style.label }}
                  </span>
                </div>
              </div>
            </RadioGroup>
          </article>
          <article class="space-y-large">
            <RadioGroup
              v-model="selectedMapLayer"
              class="space-y-large"
              @update:model-value="handleLayerChange"
            >
              <RadioGroupLabel class="block font-sans font-medium text-sm text-default-900 mt-large">
                Map layers
              </RadioGroupLabel>
              <div class="grid grid-cols-3 gap-base">
                <div
                  v-for="layer in MapboxLayerSources"
                  :key="layer.id"
                  class="col-span-1 text-center"
                >
                  <RadioGroupOption
                    v-slot="{ checked }"
                    as="template"
                    :value="layer.id"
                  >
                    <button
                      :class="[
                        'w-full cursor-pointer rounded-md text-center focus:outline-none ring-2 ring-offset-2 focus-visible:ring-primary-700',
                        checked ? 'ring-primary-500' : 'ring-transparent',
                      ]"
                    >
                      <img
                        :src="layer.image"
                        class="w-full rounded-md"
                        :alt="layer.label"
                      >
                    </button>
                  </RadioGroupOption>
                  <span class="pt-2 block w-full text-xs text-default-700">
                    {{ layer.label }}
                  </span>
                </div>
              </div>
            </RadioGroup>
          </article>
        </section>
      </PopoverPanel>
    </Transition>
  </Popover>
</template>

<script setup lang="ts">
import { ref, useSlots, watch } from 'vue';
import { SpectreButton } from '@agentsoftware/spectre-ui';
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  RadioGroup,
  RadioGroupLabel,
  RadioGroupOption,
} from '@headlessui/vue';
import MapboxStyleSources from '@/Config/MapStyleSources';
import MapboxLayerSources from '@/Config/MapboxLayerSources';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faLayerGroup } from '@awesome.me/kit-644ec404bf/icons/classic/regular';
import { MapSource } from '@/Stores/Map';
import { type MapLayer } from '@/Stores/Map';

const props = withDefaults(defineProps<{
  activeMapStyle: MapSource;
  activeMapLayer: MapLayer;
  direction?: string;
}>(), {
  direction: 'bottom',
});

const slots = useSlots();

const selectedMapStyle = ref<MapSource>(props.activeMapStyle);
const selectedMapLayer = ref<MapLayer>(props.activeMapLayer);

const emit = defineEmits(['on-style-change', 'on-layer-change']);

watch(() => props.activeMapStyle, (newValue) => {
  selectedMapStyle.value = newValue;
});

watch(() => props.activeMapLayer, (newValue) => {
  selectedMapLayer.value = newValue;
});

const handleLayerChange = (newValue: MapLayer) => {
  emit('on-layer-change', newValue);
};

const handleStyleChange = (newValue: MapSource) => {
  emit('on-style-change', newValue);
};
</script>
